// What a tenant owes, broken out the way a Maryland failure-to-pay-rent
// filing needs it: past-due RENT for a period, LATE FEES for a period, and
// a total. Pure: no database, no React. (Moved into the app from
// scripts/court-forms/arrears-fifo.js, which stays as the reference the
// first cases were checked against.)
//
// WHY OLDEST-FIRST. A balance says how much is owed, not which months are
// open or how much of it is rent rather than fees. Payments are applied to
// the oldest charge first -- which is how a tenant's money is actually
// credited -- and what is left open IS the claim; the periods fall out of
// which charges are still open.
//
// WHAT IS NEVER CLAIMED. The court's notice says the amounts do "not include
// other charges related to utilities, services, other fees, fines, and
// court costs". A charge that is neither rent nor a late fee still takes
// its turn in the queue (the tenant's money paid it, or did not), but an
// open one is listed as "not claimed" and never reaches the form. So the
// claim can be less than the balance, never more.
//
// WHY VOUCHER TENANCIES ARE SPLIT. On a housing-voucher tenancy the rent
// comes from two sources. A landlord cannot sue the TENANT for the housing
// authority's share. Each month's rent is split by what the authority
// actually paid, two queues are run, and only the tenant's reaches the
// form. When the two do not add back to the ledger the split is reported as
// unreliable and no number is offered: a figure that does not reconcile
// must not reach a court filing.
import { isRentCharge } from "./onboardingRules.js";

// to the cent (src/utils/money.js)
import { round2 } from "./money.js";
import { safeNum } from "./helpers.js";
import { allocatePayments } from "./paymentOrder.js";
export { round2 };

// A housing-authority remittance, as opposed to money from the tenant.
// ONE list of the subsidy's words, the same as the database's
// public.is_subsidy_payment_text (migration 20261006040000), which the late
// fee rule uses: HAP / HAPGC / Housing / HOC / DHCD / HCV / Carpenter's
// Shelter / voucher / Section 8. "Sigma Housing" (the company) is not one.
// tests/ftpr.test.mjs and tests/late-fee-voucher.test.mjs run the same
// sample strings through both, so the two cannot drift apart.
export const isVoucherPayment = (s) => /\bhap\b|hapgc|housing|\bhoc\b|dhcd|\bhcv\b|carpenter'?s\s+shel|voucher|section ?8/i
  .test(String(s || "").replace(/sigma\s+housing/gi, ""));

export const CHARGE_CATEGORIES = ["rent", "late_fee", "other"];
export const CATEGORY_LABEL = { rent: "Rent", late_fee: "Late fee", other: "Not rent or a late fee" };

/** rent | late_fee | other, from how the charge was posted. */
export function classifyCharge({ reference = "", memo = "", description = "", transactionType = "" } = {}) {
  const text = (String(memo || "") + " " + String(description || "")).toLowerCase();
  if (/^LATEFEE-/.test(String(reference || "")) || String(transactionType || "") === "late_fee" || /late\s*(fee|charge)/.test(text)) return "late_fee";
  if (isRentCharge({ reference, memo, description })) return "rent";
  return "other";
}

const isIso = (s) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || ""));
const pad = (n) => String(n).padStart(2, "0");
export const monthStart = (iso) => (isIso(iso) ? iso.slice(0, 8) + "01" : "");
export function monthEnd(iso) {
  if (!isIso(iso)) return "";
  const y = Number(iso.slice(0, 4)), m = Number(iso.slice(5, 7));
  return `${y}-${pad(m)}-${pad(new Date(Date.UTC(y, m, 0)).getUTCDate())}`;
}

/**
 * Journal lines on the tenant's receivable account(s) -> queue entries.
 * lines: [{ id, date, debit, credit, memo, description, reference, transactionType, createdAt }]
 * overrides: { [lineId]: "rent" | "late_fee" | "other" } -- a person's correction.
 */
export function entriesFromLines(lines, overrides = {}) {
  const cancelled = reversedPairs(lines);
  const out = [];
  for (const l of lines || []) {
    if (!l || !isIso(String(l.date || "").slice(0, 10)) || cancelled.has(l)) continue;
    const date = String(l.date).slice(0, 10);
    const label = String(l.description || l.memo || "").trim();
    const debit = round2(l.debit), credit = round2(l.credit);
    if (debit > 0) {
      const auto = classifyCharge(l);
      const chosen = overrides && CHARGE_CATEGORIES.includes(overrides[l.id]) ? overrides[l.id] : auto;
      out.push({ id: l.id, kind: "charge", date, amount: debit, label, category: chosen, autoCategory: auto, createdAt: l.createdAt || "" });
    }
    if (credit > 0) {
      // A person can say where a payment came from: an imported county
      // remittance does not always carry the authority's name (Joyce Epps,
      // 2026-10-07: four 2024 HAP payments with no label).
      const auto = isVoucherPayment(label + " " + String(l.memo || ""));
      const said = overrides && PAYMENT_SOURCES.includes(overrides[l.id]) ? overrides[l.id] : null;
      out.push({ id: l.id, kind: "payment", date, amount: credit, label, voucher: said ? said === "county" : auto, autoVoucher: auto, createdAt: l.createdAt || "" });
    }
  }
  return out;
}
export const PAYMENT_SOURCES = ["tenant", "county"];

/**
 * Money received from the tenant that is not on the ledger yet (Sahil,
 * 2026-10-07: "let me enter the amount I received along with the date and
 * make the notice with the reduced balance"). Each one counts as a payment
 * from the tenant on its date, applied by the same rule as any other, so
 * the rent and late-fee periods on the form follow. Nothing is posted to
 * the books here; the notice records that it was counted.
 */
export function unbookedEntries(list) {
  return (list || [])
    .filter(u => u && /^\d{4}-\d{2}-\d{2}$/.test(String(u.date || "")) && round2(safeNum(u.amount)) > 0)
    .map((u, i) => ({ id: "unbooked-" + (u.id ?? i), kind: "payment", date: String(u.date).slice(0, 10), amount: round2(safeNum(u.amount)), label: "Received " + usDateOf(String(u.date).slice(0, 10)) + " (not yet in the books)", voucher: false, autoVoucher: false, createdAt: "" }));
}
const usDateOf = (iso) => (/^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso.slice(5, 7) + "/" + iso.slice(8, 10) + "/" + iso.slice(0, 4) : iso);


/**
 * The tenant's share in effect for a month, from the dated history: the
 * latest row on or before the month's first day; a month before the first
 * row gets the first row; no history, or a row without a share, falls back
 * to the figure on the record. Returns (yyyy-mm) -> number.
 */
export function portionByMonth(history, fallback = 0) {
  const rows = (history || []).filter(h => h && /^\d{4}-\d{2}-\d{2}/.test(String(h.effective_from || "")))
    .map(h => ({ from: String(h.effective_from).slice(0, 10), portion: h.tenant_portion === null || h.tenant_portion === undefined ? null : Number(h.tenant_portion) }))
    .sort((a, b) => a.from.localeCompare(b.from));
  return (ym) => {
    if (!rows.length) return fallback;
    const day = String(ym || "").slice(0, 7) + "-01";
    let pick = null;
    for (const r of rows) { if (r.from <= day) pick = r; else break; }
    if (!pick) pick = rows[0];
    // a row with no share: the nearest earlier row that has one
    if (pick.portion === null) { const i = rows.indexOf(pick); for (let k = i; k >= 0; k--) if (rows[k].portion !== null) { pick = rows[k]; break; } }
    return pick.portion === null ? fallback : pick.portion;
  };
}

export const PAYMENT_SOURCE_LABEL = { tenant: "From the tenant", county: "From the county" };

// A reversal cancels the one entry it reverses; it is not money from the
// tenant. The app writes a reversal with the reference REV-<original
// reference> (Accounting.js), and the re-onboard clean-up wrote
// REVDUP-<original entry id>. A reversal credit that names a charge on the
// same ledger for the same amount takes that charge out of the queue along
// with itself. One that names nothing on this ledger is left as a credit,
// as before. (Amanda Mathews, 2026-10-07: a duplicate first month's rent
// and its reversal were read as a second month of rent and a payment, and
// the payment went against the deposit line instead -- the rent was claimed
// twice.)
export function reversedPairs(lines) {
  const cancelled = new Set();
  const charges = (lines || []).filter(l => l && round2(l.debit) > 0);
  const byRef = new Map(), byEntry = new Map();
  for (const c of charges) {
    const ref = String(c.reference || "").trim();
    if (ref) { if (!byRef.has(ref)) byRef.set(ref, []); byRef.get(ref).push(c); }
    const je = String(c.journalEntryId || "").trim();
    if (je) { if (!byEntry.has(je)) byEntry.set(je, []); byEntry.get(je).push(c); }
  }
  for (const r of lines || []) {
    if (!r || !(round2(r.credit) > 0)) continue;
    const m = String(r.reference || "").match(/^(REV|REVDUP)-(.+)$/);
    if (!m) continue;
    const candidates = (m[1] === "REVDUP" ? byEntry.get(m[2]) : byRef.get(m[2])) || [];
    const orig = candidates.find(c => !cancelled.has(c) && Math.abs(round2(c.debit) - round2(r.credit)) < 0.005);
    if (!orig) continue;
    cancelled.add(orig); cancelled.add(r);
  }
  return cancelled;
}

const byAge = (a, b) => a.date.localeCompare(b.date) || String(a.createdAt).localeCompare(String(b.createdAt)) || String(a.id ?? "").localeCompare(String(b.id ?? ""), undefined, { numeric: true });

// Money is applied by the one shared rule (paymentOrder.js): in time order,
// a payment settles the charges open that day month by month, oldest month
// first -- that month's late fees, damages and other charges, then its rent,
// then the next month -- and anything left over is carried to the next
// charge (Sahil, 2026-10-07). This used to pool every payment and settle
// the whole queue oldest-first.
function settle(queue, pays) {
  const { charges, credit } = allocatePayments([...queue.map(c => ({ ...c, kind: "charge" })), ...(pays || []).map(p => ({ ...p, kind: "payment" }))]);
  const byId = new Map(charges.map(c => [String(c.id) + "|" + c.date + "|" + c.amount + "|" + (c.share || ""), c]));
  for (const c of queue) { const r = byId.get(String(c.id) + "|" + c.date + "|" + c.amount + "|" + (c.share || "")); if (r) c.open = r.open; }
  return credit;
}

function summarise(queue, creditLeft, voucherTenancy) {
  const open = queue.filter(c => c.open > 0.004);
  const pick = (cat) => open.filter(c => c.category === cat);
  const span = (list) => {
    if (!list.length) return { amount: 0, from: "", to: "", months: 0 };
    const months = new Set(list.map(c => c.date.slice(0, 7)));
    return { amount: round2(list.reduce((s, c) => s + c.open, 0)), from: monthStart(list[0].date), to: monthEnd(list[list.length - 1].date), months: months.size };
  };
  const rent = span(pick("rent")), lateFees = span(pick("late_fee")), other = pick("other");
  return {
    voucherTenancy,
    rent, lateFees,
    notClaimed: { amount: round2(other.reduce((s, c) => s + c.open, 0)), count: other.length },
    total: round2(rent.amount + lateFees.amount),
    creditLeft,
    open: open.map(c => ({ id: c.id, date: c.date, label: c.label, category: c.category, autoCategory: c.autoCategory || c.category, share: c.share || "tenant", charged: c.amount, stillOpen: c.open })),
  };
}

// What the authority paid, BY THE MONTH OF RENT IT COVERS. A remittance
// larger than a month's rent is for several months (the county paid June,
// July and August in one August payment on Amanda Mathews' ledger), so it
// is spread evenly over that many months it has not yet covered, ending
// with the month it was paid in. A remittance when nothing earlier is open
// is for the next uncovered month. hapFor(m) carries the last known amount
// forward to a month it has not paid yet, so that month does not silently
// become the tenant's debt.
// Which charge is THE month's rent when several count as rent: one posted
// as rent outranks one a person reclassified; then the larger.
const rentRank = (c) => ((c.autoCategory || c.category) === "rent" ? 1e12 : 0) + Number(c.amount || 0);

export function countyByMonth(charges, payments) {
  const rentMonths = [...new Set(charges.filter(c => c.category === "rent").map(c => c.date.slice(0, 7)))].sort();
  // rentOf: everything charged as rent in the month (the ceiling for the
  // authority's share). rentUnitOf: THE month's rent -- the largest single
  // rent charge -- which is what the authority's share is worked from. A
  // second rent-type charge in a month (a re-posted rent, a deposit a
  // person reclassified as rent) is the tenant's alone; counting it as
  // rent the county also owes part of doubled June for Amanda Mathews
  // (2026-10-07) and invented a $1,377 county shortfall.
  const rentOf = new Map(), rentUnitOf = new Map();
  for (const c of charges) if (c.category === "rent") {
    const m = c.date.slice(0, 7);
    rentOf.set(m, round2((rentOf.get(m) || 0) + c.amount));
    rentUnitOf.set(m, Math.max(rentUnitOf.get(m) || 0, c.amount));
  }
  const hapByMonth = new Map();
  for (const p of [...payments].sort((a, b) => a.date.localeCompare(b.date))) {
    if (!p.voucher) continue;
    const k = p.date.slice(0, 7), amount = round2(p.amount);
    const uncovered = rentMonths.filter(m => !hapByMonth.has(m));
    const before = uncovered.filter(m => m <= k), after = uncovered.filter(m => m > k);
    let months;
    if (before.length) {
      const n = Math.max(1, Math.min(before.length, Math.ceil((amount - 0.005) / Math.max(rentUnitOf.get(k) || rentUnitOf.get(before[before.length - 1]) || amount, 0.01))));
      months = before.slice(-n);
    } else if (after.length) months = [after[0]];
    else continue; // every rent month is covered: the money stays the authority's unapplied credit
    const each = round2(amount / months.length);
    months.forEach((m, i) => hapByMonth.set(m, i === months.length - 1 ? round2(amount - each * (months.length - 1)) : each));
  }
  const monthsSeen = [...hapByMonth.keys()].sort();
  const hapFor = (k) => {
    if (hapByMonth.has(k)) return hapByMonth.get(k);
    const prior = monthsSeen.filter(m => m < k);
    return prior.length ? hapByMonth.get(prior[prior.length - 1]) : 0;
  };
  return { rentMonths, rentOf, rentUnitOf, hapByMonth, hapFor, monthsSeen };
}

/**
 * The claim for a court notice, read from the MOST RECENT month backwards
 * (Sahil, 2026-10-07: "start from the most current and go back, instead of
 * looking at 2024 entries in 2026").
 *
 * The balance on the ledger is a fact: charges less payments. The notice
 * only has to say which recent charges it is made of. Starting with the
 * latest month, each month's charges are counted as open -- the housing
 * authority's unpaid share first, then the tenant's share of the rent,
 * then that month's late fees and other charges -- and taken off the
 * balance, month by month back in time, until the balance is used up. A
 * partly open month opens its rent first: fees are paid first going
 * forward (paymentOrder.js), so they are the first to be gone. Nothing
 * older than the last month reached is read, so a mislabelled payment in
 * 2024 cannot shift a claim made in 2026, and the result always ties to
 * the ledger by construction.
 *
 * On a voucher tenancy a month's authority share is what the authority
 * paid for it when that is more than rent less the tenant's portion on
 * file (older months ran on an older portion); a month it has not paid
 * yet uses the portion on file, or its last known amount. Only the
 * tenant's share reaches the form. The trade-off, accepted: an old
 * shortfall the authority never made good sits in the balance and lands
 * on the tenant's oldest open month; the dialog lists every charge it
 * counted, and a person can say a payment was the county's.
 *
 * entries: from entriesFromLines. opts: { voucher, tenantPortion }.
 * Returns the same shape as fifoArrears (rent, lateFees, notClaimed,
 * total, balance, open, voucherTenancy, voucherSide, reconciles: true),
 * plus payments: the payments in the months reached, newest first, for
 * the "from the county" correction.
 */
const monthsBack = (ym, n) => { const y = Number(ym.slice(0, 4)), m = Number(ym.slice(5, 7)) - 1 - n; const d = new Date(Date.UTC(y, m, 1)); return d.getUTCFullYear() + "-" + pad(d.getUTCMonth() + 1); };
const paymentWindowStart = (latestMonth, oldestReached) => {
  if (!latestMonth) return "9999-99";
  const six = monthsBack(latestMonth, 5);
  return oldestReached && oldestReached < six ? oldestReached : six;
};
export function walkBackArrears(entries, opts = {}) {
  const voucher = !!opts.voucher;
  const charges = (entries || []).filter(e => e && e.kind === "charge" && round2(e.amount) > 0).map(e => ({ ...e, amount: round2(e.amount), category: CHARGE_CATEGORIES.includes(e.category) ? e.category : "other" })).sort(byAge);
  const payments = (entries || []).filter(e => e && e.kind === "payment" && round2(e.amount) > 0).sort((a, b) => a.date.localeCompare(b.date));
  const balance = round2(charges.reduce((s, c) => s + c.amount, 0) - payments.reduce((s, p) => s + round2(p.amount), 0));
  const portion = round2(Number(opts.tenantPortion) || 0);
  // The tenant's share for a month: the dated history when the caller has
  // one (ftprData.portionByMonth), else the figure on the record.
  const portionOf = typeof opts.tenantPortionOf === "function" ? (m) => round2(Number(opts.tenantPortionOf(m)) || 0) : () => portion;
  const { rentOf, rentUnitOf, hapByMonth, hapFor, monthsSeen } = voucher ? countyByMonth(charges, payments) : { rentOf: new Map(), rentUnitOf: new Map(), hapByMonth: new Map(), hapFor: () => 0, monthsSeen: [] };

  const open = [];
  let remaining = balance, countyOpen = 0, oldestMonth = "";
  const months = [...new Set(charges.map(c => c.date.slice(0, 7)))].sort().reverse();
  for (const m of months) {
    if (remaining <= 0.004) break;
    oldestMonth = m;
    const inMonth = charges.filter(c => c.date.slice(0, 7) === m);
    const rents = inMonth.filter(c => c.category === "rent"), rest = inMonth.filter(c => c.category !== "rent");
    // The authority's share of this month, given to the earlier rent charge first.
    let authority = 0;
    // The month's rent charge proper (the largest), which carries the county's share.
    const main = rents.length ? rents.reduce((a, c) => (rentRank(c) > rentRank(a) ? c : a), rents[0]) : null;
    if (voucher && rents.length) {
      const rent = rentUnitOf.get(m) || 0, share = portionOf(m);
      const expected = share > 0 ? Math.max(0, rent - share) : hapFor(m);
      const observed = hapByMonth.has(m) ? hapByMonth.get(m) : 0;
      authority = round2(Math.min(rentOf.get(m) || 0, Math.max(expected, observed)));
      const unpaid = round2(Math.max(0, authority - observed));
      if (unpaid > 0) {
        const take = round2(Math.min(unpaid, remaining));
        open.push({ id: main.id, date: main.date, label: main.label, category: "rent", autoCategory: main.autoCategory || "rent", share: "voucher", charged: authority, stillOpen: take });
        countyOpen = round2(countyOpen + take); remaining = round2(remaining - take);
      }
    }
    // Then the tenant's rent, newest charge first; then fees and other charges.
    // The county's share goes to the month's rent charge proper first, then
    // (only if it is larger than that charge) to the others, oldest first.
    const items = [];
    let left = authority;
    const order = main ? [main, ...rents.filter(c => c !== main)] : rents;
    const tenantOf = new Map();
    for (const c of order) { const a = round2(Math.min(left, c.amount)); left = round2(left - a); tenantOf.set(c, round2(c.amount - a)); }
    for (const c of rents) { const t = tenantOf.get(c) || 0; if (t > 0) items.push({ c, amount: t }); }
    items.reverse();
    for (const c of [...rest].reverse()) items.push({ c, amount: c.amount });
    for (const { c, amount } of items) {
      if (remaining <= 0.004) break;
      const take = round2(Math.min(amount, remaining));
      open.push({ id: c.id, date: c.date, label: c.label, category: c.category, autoCategory: c.autoCategory || c.category, share: "tenant", charged: amount, stillOpen: take });
      remaining = round2(remaining - take);
    }
  }
  open.sort((a, b) => a.date.localeCompare(b.date) || String(a.id ?? "").localeCompare(String(b.id ?? ""), undefined, { numeric: true }));

  const tenantOpen = open.filter(c => c.share === "tenant");
  const span = (list) => {
    if (!list.length) return { amount: 0, from: "", to: "", months: 0 };
    return { amount: round2(list.reduce((s, c) => s + c.stillOpen, 0)), from: monthStart(list[0].date), to: monthEnd(list[list.length - 1].date), months: new Set(list.map(c => c.date.slice(0, 7))).size };
  };
  const rent = span(tenantOpen.filter(c => c.category === "rent")), lateFees = span(tenantOpen.filter(c => c.category === "late_fee"));
  const other = tenantOpen.filter(c => c.category === "other");
  const county = open.filter(c => c.share === "voucher");
  const out = {
    voucherTenancy: voucher, rent, lateFees,
    notClaimed: { amount: round2(other.reduce((s, c) => s + c.stillOpen, 0)), count: other.length },
    total: round2(rent.amount + lateFees.amount),
    balance, creditLeft: 0, drift: 0, reconciles: true, open,
    // The payments a person may need to correct, newest first: those in the
    // months the walk reached, and in any case the last six months -- so a
    // correction that shortens the walk does not hide the other payments.
    payments: payments.filter(p => p.date.slice(0, 7) >= paymentWindowStart(months[0], oldestMonth)).reverse()
      .map(p => ({ id: p.id, date: p.date, amount: round2(p.amount), label: p.label, voucher: !!p.voucher, autoVoucher: p.autoVoucher !== undefined ? !!p.autoVoucher : !!p.voucher })),
    from: oldestMonth ? oldestMonth + "-01" : "",
  };
  if (voucher) {
    out.voucherSide = {
      owedByAuthority: countyOpen, months: new Set(county.map(c => c.date.slice(0, 7))).size,
      from: county.length ? monthStart(county[0].date) : "", to: county.length ? monthEnd(county[county.length - 1].date) : "",
      unappliedCredit: 0, monthlyShareObserved: monthsSeen.length ? hapByMonth.get(monthsSeen[monthsSeen.length - 1]) : 0,
    };
  }
  return out;
}

/**
 * The reference engine: the whole ledger replayed oldest-first. Kept for
 * the AR reports' cross-checks and the tests; the court notice reads the
 * ledger with walkBackArrears instead (2026-10-07).
 * entries: from entriesFromLines. opts.voucher: split rent between the
 * housing authority and the tenant.
 * Returns { rent, lateFees, notClaimed, total, balance, open, creditLeft,
 *           voucherTenancy, reconciles, unreliable?, blockedReason?, voucherSide? }
 */
export function fifoArrears(entries, opts = {}) {
  const charges = (entries || []).filter(e => e && e.kind === "charge" && round2(e.amount) > 0).map(e => ({ ...e, amount: round2(e.amount), category: CHARGE_CATEGORIES.includes(e.category) ? e.category : "other" })).sort(byAge);
  const payments = (entries || []).filter(e => e && e.kind === "payment" && round2(e.amount) > 0).sort((a, b) => a.date.localeCompare(b.date));
  const balance = round2(charges.reduce((s, c) => s + c.amount, 0) - payments.reduce((s, p) => s + round2(p.amount), 0));

  if (!opts.voucher) {
    const q = charges.map(c => ({ ...c, open: c.amount }));
    const left = settle(q, payments);
    return { ...summarise(q, left, false), balance, reconciles: true };
  }

  // ── voucher tenancy ──────────────────────────────────────────────────
  // What the authority paid, BY THE MONTH OF RENT IT COVERS: the observed
  // voucher share. A remittance larger than a month's rent is for several
  // months (the county paid June, July and August in one August payment on
  // Amanda Mathews' ledger), so it is spread evenly over that many months
  // it has not yet covered, ending with the month it was paid in. A
  // remittance when nothing earlier is open is for the next uncovered
  // month. A month it has not paid yet carries the last known amount
  // forward, so that month does not silently become the tenant's debt.
  const { rentMonths, rentOf, rentUnitOf, hapByMonth, hapFor, monthsSeen } = countyByMonth(charges, payments);

  // The authority's share of a month's rent. With the tenant's portion on
  // the record (the late fee rule's figure) the expected share is rent less
  // that portion; when the authority actually paid MORE for a month, what it
  // paid is its share -- the portion on file is today's figure, and an
  // earlier month ran on an earlier one (Amanda Mathews: $12 a month until
  // September, $719 from October). When it paid less, the shortfall stays
  // the authority's, not the tenant's. Without a portion on file, the
  // inference from what it remitted. One rent a month: a month with two
  // rent charges gives the authority's share to the earlier charge first.
  const portion = round2(Number(opts.tenantPortion) || 0);
  const portionOf = typeof opts.tenantPortionOf === "function" ? (m) => round2(Number(opts.tenantPortionOf(m)) || 0) : () => portion;
  const authorityFor = new Map();
  for (const m of rentMonths) {
    const rent = rentUnitOf.get(m), share = portionOf(m);
    const expected = share > 0 ? Math.max(0, rent - share) : hapFor(m);
    const observed = hapByMonth.has(m) ? hapByMonth.get(m) : 0;
    authorityFor.set(m, round2(Math.min(rentOf.get(m), Math.max(expected, observed))));
  }
  // The county's share goes to the month's rent charge proper (the largest) first.
  const mainOf = new Map();
  for (const c of charges) if (c.category === "rent") { const m = c.date.slice(0, 7); if (!mainOf.has(m) || rentRank(c) > rentRank(mainOf.get(m))) mainOf.set(m, c); }
  const tenantQ = [], voucherQ = [];
  const rentCharges = charges.filter(c => c.category === "rent");
  const splitOrder = [...rentCharges].sort((a, b) => a.date.slice(0, 7).localeCompare(b.date.slice(0, 7)) || ((mainOf.get(a.date.slice(0, 7)) === a ? 0 : 1) - (mainOf.get(b.date.slice(0, 7)) === b ? 0 : 1)) || byAge(a, b));
  const authorityOf = new Map();
  for (const c of splitOrder) {
    const m = c.date.slice(0, 7);
    const a = round2(Math.min(authorityFor.get(m) || 0, c.amount));
    authorityFor.set(m, round2((authorityFor.get(m) || 0) - a)); authorityOf.set(c, a);
  }
  for (const c of charges) {
    if (c.category !== "rent") { tenantQ.push({ ...c, open: c.amount, share: "tenant" }); continue; }
    const authority = authorityOf.get(c) || 0;
    const tenantShare = round2(c.amount - authority);
    if (authority > 0) voucherQ.push({ ...c, amount: authority, open: authority, share: "voucher" });
    if (tenantShare > 0) tenantQ.push({ ...c, amount: tenantShare, open: tenantShare, share: "tenant" });
  }
  // Two queues, each settled only by its own source of money.
  const tenantLeft = settle(tenantQ, payments.filter(p => !p.voucher));
  const voucherLeft = settle(voucherQ, payments.filter(p => p.voucher));

  const out = { ...summarise(tenantQ, tenantLeft, true), balance };
  const vOpen = voucherQ.filter(c => c.open > 0.004);
  out.voucherSide = {
    owedByAuthority: round2(vOpen.reduce((s, c) => s + c.open, 0)),
    months: new Set(vOpen.map(c => c.date.slice(0, 7))).size,
    from: vOpen.length ? monthStart(vOpen[0].date) : "", to: vOpen.length ? monthEnd(vOpen[vOpen.length - 1].date) : "",
    unappliedCredit: voucherLeft,
    monthlyShareObserved: monthsSeen.length ? hapByMonth.get(monthsSeen[monthsSeen.length - 1]) : 0,
  };

  // RECONCILIATION GATE. The split of each month's rent is INFERRED from
  // what the authority remitted. When that inference is wrong, one queue
  // ends with money left over while the other still has charges open, and
  // the two no longer add back to the real balance. (On one real tenancy it
  // produced a tenant claim of $0 against a true balance near $6,000.)
  const tenantOpenAll = round2(out.total + out.notClaimed.amount);
  const splitTotal = round2(tenantOpenAll + out.voucherSide.owedByAuthority);
  const expected = opts.ledgerBalance !== undefined && opts.ledgerBalance !== null ? round2(opts.ledgerBalance) : balance;
  out.drift = round2(splitTotal - Math.max(expected, 0));
  out.reconciles = Math.abs(out.drift) < 0.01;
  if (!out.reconciles && splitTotal > 0) {
    out.unreliable = true;
    out.blockedReason = "The split between the tenant and the housing authority does not add up: tenant "
      + tenantOpenAll.toFixed(2) + " + authority " + out.voucherSide.owedByAuthority.toFixed(2) + " = " + splitTotal.toFixed(2)
      + ", but the ledger balance is " + expected.toFixed(2) + ". The tenant's own share for each month is not recorded, so it cannot be worked out from the ledger alone.";
    out.needed = ["the tenant's monthly share of the rent, with the date each change took effect", "whether a month with no housing-authority payment was skipped, or the contract ended"];
  } else out.reconciles = true;
  return out;
}

/**
 * Late fees that look higher than the cap. Maryland: 5% of the rent due for
 * the period [LAW]. Returns the open late-fee charges over the cap.
 */
export function lateFeesOverCap(open, monthlyRent, capPct = 5) {
  const cap = round2((Number(monthlyRent) || 0) * capPct / 100);
  if (!(cap > 0)) return [];
  return (open || []).filter(c => c.category === "late_fee" && round2(c.charged) > cap + 0.005).map(c => ({ ...c, cap }));
}

/** A person's own figures, when the ledger cannot give them (a voucher split that does not reconcile). */
export function manualClaim({ rent = 0, rentFrom = "", rentTo = "", lateFees = 0, feesFrom = "", feesTo = "" } = {}) {
  const r = round2(rent), f = round2(lateFees);
  const errors = [];
  if (r < 0 || f < 0) errors.push("Amounts cannot be negative.");
  if (r > 0 && (!isIso(rentFrom) || !isIso(rentTo))) errors.push("Enter the period the unpaid rent covers.");
  if (r > 0 && isIso(rentFrom) && isIso(rentTo) && rentTo < rentFrom) errors.push("The rent period ends before it starts.");
  if (f > 0 && (!isIso(feesFrom) || !isIso(feesTo))) errors.push("Enter the period the late fees cover.");
  if (f > 0 && isIso(feesFrom) && isIso(feesTo) && feesTo < feesFrom) errors.push("The late-fee period ends before it starts.");
  if (r + f <= 0) errors.push("Nothing is claimed.");
  return {
    ok: errors.length === 0, errors,
    claim: { rent: { amount: r, from: r > 0 ? rentFrom : "", to: r > 0 ? rentTo : "", months: 0 }, lateFees: { amount: f, from: f > 0 ? feesFrom : "", to: f > 0 ? feesTo : "", months: 0 }, notClaimed: { amount: 0, count: 0 }, total: round2(r + f), open: [], manual: true, reconciles: true },
  };
}
